import { fetchLocalCheckoutConfig } from './api';

const DISABLED = Object.freeze({ enabled: false });

export const LOCAL_CHECKOUT_UNAVAILABLE = 'local-checkout-config-unavailable';

const unavailable = cause =>
  Object.assign(new Error('Checkout could not be loaded, please try again'), {
    code: LOCAL_CHECKOUT_UNAVAILABLE,
    cause,
  });

const parseConfig = c => {
  if (!c || typeof c.enabled !== 'boolean') {
    throw unavailable(new Error('Unexpected /api/orders/checkout-config response'));
  }
  if (!c.enabled) return DISABLED;
  if (typeof c.publishableKey !== 'string' || !c.publishableKey) {
    throw unavailable(new Error('Local checkout is on without a Stripe key'));
  }
  return { enabled: true, publishableKey: c.publishableKey };
};

// One lookup per signed-in user (guests share ''), so a guest who signs up at the pay step is
// looked up again with their new account.
const configByUser = new Map();

/** Forgets every answer (logout). */
export const resetLocalCheckoutConfig = () => configByUser.clear();

/**
 * `{ enabled, publishableKey }` for the visitor. Only an explicit `enabled: false` from our server
 * means the legacy Sharetribe checkout; a failed lookup or a malformed answer rejects with
 * `code: LOCAL_CHECKOUT_UNAVAILABLE`, so callers neither start an order nor pick a Stripe key on
 * a guess. A failure is not cached: the next call asks again.
 */
export const getLocalCheckoutConfig = userId => {
  if (typeof window === 'undefined') {
    return Promise.reject(unavailable(new Error('Checkout config is only looked up in the browser')));
  }
  const key = userId || '';
  if (!configByUser.has(key)) {
    const lookup = Promise.resolve()
      .then(() => fetchLocalCheckoutConfig())
      .then(parseConfig, e => {
        throw unavailable(e);
      });
    lookup.catch(() => {
      if (configByUser.get(key) === lookup) configByUser.delete(key);
    });
    configByUser.set(key, lookup);
  }
  return configByUser.get(key);
};

export const isLocalCheckoutUnavailable = e => e?.code === LOCAL_CHECKOUT_UNAVAILABLE;

export const isLocalOrder = tx => tx?.attributes?.orderSource === 'local';
